import { Component, OnInit, Input } from '@angular/core';
import { Amline } from 'src/app/model/quote/amline/amline.model';
import * as hbDisplay from '../../amlin-horsebox-display.util';
import { environment } from '../../../../../environments/environment';
import { LocalStorageService } from 'ngx-webstorage';
import { SDTKService } from '../../../sdtk/sdtk.service';
import { ExcessProtectService } from '../../../excess-protect/excess.service';

@Component({
  selector: 'app-am-overview',
  templateUrl: './am-overview.component.html',
  styleUrls: ['./am-overview.component.css', '../../../quote.component.css']
})
export class AmOverviewComponent implements OnInit {
  quote;
  quoteApplicationData = new Amline();
  CoverTo;

  // Linked SDTK data
  sdtkQuote: any = null;
  sdtkCoverLimitName: string = '';
  sdtkStorageName: string = '';
  sdtkPremiumExclIpt: number = 0;
  sdtkIpt: number = 0;
  sdtkTotalInclIpt: number = 0;
  sdtkIsTbc: boolean = false;

  // Linked EP data
  epQuote: any = null;
  epCoverLimitName: string = '';
  epPremiumExclIpt: number = 0;
  epIpt: number = 0;
  epTotalInclIpt: number = 0;

  @Input() set quoteDetails(value: any) {
    if (value) {
      this.quote = value;
      this.quoteApplicationData = value.application;
      let maxDate = new Date(value.application.inception_date).setFullYear(new Date(value.application.inception_date).getFullYear() + 1);
      this.CoverTo = new Date(new Date(maxDate).getTime() - (24*60*60*1000));
    }
  }

  @Input() set sdtkQuoteDetails(value: any) {
    if (value) {
      this.sdtkQuote = value;
      if (value.sdtk_questions) {
        this.sdtkStorageName = value.sdtk_questions.storage_location == 1 ? 'Building' : 'Shipping Container';
      }
      this.applySdtkFinancials();
      this.loadSdtkCoverLimit();
    }
  }

  @Input() set epQuoteDetails(value: any) {
    if (value) {
      this.epQuote = value;
      this.applyEpFinancials();
      this.loadEpCoverLimit();
    }
  }

  constructor(private sessionSt: LocalStorageService, private sdtkService: SDTKService, private epService: ExcessProtectService) { }

  UK_HB_INSURANCE_COMPANY_ID = '';
  isAdmin = false;
  ngOnInit(): void {
    this.UK_HB_INSURANCE_COMPANY_ID = environment.UK_HB_INSURANCE_COMPANY_ID;

    if (this.sessionSt.retrieve('is_admin')) {
      this.isAdmin = true;
    }
  }

  getVehicleOvernightLines(v: any): string[] {
    return hbDisplay.getVehicleOvernightLines(v);
  }

  isKeptOvernightAtProposer(v: any): boolean {
    return hbDisplay.isKeptOvernightAtProposer(v);
  }

  correspondenceAddressLine(app: any): string | null {
    return hbDisplay.getCorrespondenceLine(app);
  }

  // SDTK financial calculations
  private applySdtkFinancials() {
    if (!this.sdtkQuote) return;
    const fd = this.sdtkQuote.financial_details;
    if (fd) {
      this.sdtkIpt = Number(fd.ipt_value) || 0;
      this.sdtkPremiumExclIpt = Number(fd.breakdown_gross) || 0;
      this.sdtkTotalInclIpt = Number(fd.total_policy_cost) || (this.sdtkPremiumExclIpt + this.sdtkIpt);
    }
    // Fallback: check top-level properties (some API responses put them here)
    if (!this.sdtkPremiumExclIpt && this.sdtkQuote.breakdown_gross) {
      this.sdtkPremiumExclIpt = Number(this.sdtkQuote.breakdown_gross) || 0;
    }
    if (!this.sdtkIpt && this.sdtkQuote.ipt_value) {
      this.sdtkIpt = Number(this.sdtkQuote.ipt_value) || 0;
    }
    if (!this.sdtkTotalInclIpt && this.sdtkQuote.total_policy_cost) {
      this.sdtkTotalInclIpt = Number(this.sdtkQuote.total_policy_cost) || 0;
    }
    // If we have total but not breakdown, calculate
    if (this.sdtkTotalInclIpt && !this.sdtkPremiumExclIpt && !this.sdtkIpt) {
      // Estimate IPT at 12%
      this.sdtkPremiumExclIpt = Math.round((this.sdtkTotalInclIpt / 1.12) * 100) / 100;
      this.sdtkIpt = Math.round((this.sdtkTotalInclIpt - this.sdtkPremiumExclIpt) * 100) / 100;
    }
  }

  private loadSdtkCoverLimit() {
    if (!this.sdtkQuote || !this.sdtkQuote.sdtk_option_id) return;
    this.sdtkService.getSDTKRatingOptions().subscribe(
      (response: any) => {
        if (response.success && response.result) {
          // Use == for loose comparison (API may return string or number)
          const option = response.result.find(opt => opt.id == this.sdtkQuote.sdtk_option_id);
          if (option) {
            this.sdtkCoverLimitName = option.name;
            // Check if over 10000 → TBC
            const amount = parseFloat(option.name.replace(/[£,]/g, ''));
            if (amount > 10000) {
              this.sdtkIsTbc = true;
            }
          }
        }
      }
    );
  }

  // EP financial calculations
  private applyEpFinancials() {
    if (!this.epQuote) return;
    const fd = this.epQuote.financial_details;
    if (fd) {
      this.epIpt = Number(fd.ipt_value) || 0;
      this.epPremiumExclIpt = Number(fd.breakdown_gross) || 0;
      this.epTotalInclIpt = Number(fd.total_policy_cost) || (this.epPremiumExclIpt + this.epIpt);
    }
    // Fallback: check top-level properties
    if (!this.epPremiumExclIpt && this.epQuote.breakdown_gross) {
      this.epPremiumExclIpt = Number(this.epQuote.breakdown_gross) || 0;
    }
    if (!this.epIpt && this.epQuote.ipt_value) {
      this.epIpt = Number(this.epQuote.ipt_value) || 0;
    }
    if (!this.epTotalInclIpt && this.epQuote.total_policy_cost) {
      this.epTotalInclIpt = Number(this.epQuote.total_policy_cost) || 0;
    }
    // If we have total but not breakdown, calculate
    if (this.epTotalInclIpt && !this.epPremiumExclIpt && !this.epIpt) {
      this.epPremiumExclIpt = Math.round((this.epTotalInclIpt / 1.12) * 100) / 100;
      this.epIpt = Math.round((this.epTotalInclIpt - this.epPremiumExclIpt) * 100) / 100;
    }
  }

  private loadEpCoverLimit() {
    if (!this.epQuote || !this.epQuote.excess_protect_cover_id) return;
    this.epService.getExcessProtectOptions().subscribe(
      (response: any) => {
        if (response && response.result) {
          // Use == for loose comparison (API may return string or number)
          const option = response.result.find(opt => opt.id == this.epQuote.excess_protect_cover_id);
          if (option) {
            this.epCoverLimitName = option.name;
          }
        }
      }
    );
  }

}
